import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plug, Copy, Trash2, Plus, AlertCircle, CheckCircle2, Code, Webhook, Key, Zap } from "lucide-react";
import { PageHeader, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";
import SubscriptionGate from "../components/SubscriptionGate";

export default function Integrations() {
  const { lang } = useApp();
  return (
    <SubscriptionGate featureName={lang === "fr" ? "Les Intégrations API" : "API Integrations"}>
      <IntegrationsInner />
    </SubscriptionGate>
  );
}

function IntegrationsInner() {
  const { lang } = useApp();
  const [keys, setKeys] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showCurl, setShowCurl] = useState(false);
  const [autoConnecting, setAutoConnecting] = useState(false);

  const load = () => axios.get(`${API}/integration/api-keys`).then(({ data }) => setKeys(data));
  useEffect(() => { load(); }, []);

  const hasAutoKey = !!(keys && keys.some((k) => k.auto_provisioned && !k.revoked));

  const autoConnect = async () => {
    setAutoConnecting(true);
    try {
      const { data } = await axios.post(`${API}/integration/auto-provision`);
      if (data.created && data.key) setNewKey(data.key);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.detail || (lang === "fr" ? "Erreur lors de la connexion auto" : "Auto-connect error"));
    } finally { setAutoConnecting(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/integration/api-keys`, { name, webhook_url: webhookUrl });
      setNewKey(data);
      setName(""); setWebhookUrl("");
      load();
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm(lang === "fr" ? "Révoquer cette clé ? L'ERP ne pourra plus se connecter." : "Revoke this key?")) return;
    await axios.delete(`${API}/integration/api-keys/${id}`);
    load();
  };

  const copyKey = async () => {
    if (newKey?.key) {
      await navigator.clipboard.writeText(newKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const baseUrl = window.location.origin;
  const apiBase = `${baseUrl}/api/integration/v1`;

  const T = lang === "fr" ? {
    title: "Intégrations & API",
    sub: "Connectez PROGÈRE Devis AI à votre futur ERP PROGÈRE Gestion Chantier ou à d'autres systèmes via une API REST sécurisée.",
    create_title: "Nouvelle clé API",
    name_label: "Nom (ex: ERP Chantier Production)",
    webhook_label: "URL Webhook (optionnel — pour recevoir les événements en temps réel)",
    webhook_help: "Événements envoyés : quote.accepted · invoice.created · invoice.paid",
    create_btn: "Générer la clé",
    new_key_title: "Votre clé API",
    new_key_warn: "⚠️ Copiez-la maintenant — pour des raisons de sécurité, elle ne sera plus jamais affichée en clair.",
    copy: "Copier",
    copied: "Copié ✓",
    existing: "Clés existantes",
    no_keys: "Aucune clé API. Créez votre première clé ci-dessus.",
    last_used: "Dernière utilisation",
    never: "Jamais",
    revoked: "Révoquée",
    revoke: "Révoquer",
    created: "Créée le",
    docs_title: "Documentation API",
    docs_sub: "Endpoints disponibles pour votre ERP",
    base_url: "URL de base",
    auth_help: "Authentification : en-tête HTTP",
    show_examples: "Voir des exemples cURL",
    hide_examples: "Masquer les exemples",
    endpoints: [
      { method: "GET", path: "/me", desc: "Informations du compte connecté" },
      { method: "GET", path: "/quotes", desc: "Liste des soumissions (param. status optionnel)" },
      { method: "GET", path: "/invoices", desc: "Liste des factures avec statut calculé" },
      { method: "GET", path: "/clients", desc: "Liste des clients" },
      { method: "GET", path: "/projects", desc: "Liste des projets" },
    ],
    events_title: "Événements webhook",
    events: [
      { name: "quote.accepted", desc: "Un client a accepté une soumission" },
      { name: "invoice.created", desc: "Une facture a été générée" },
      { name: "invoice.paid", desc: "Une facture a été entièrement payée" },
    ],
  } : {
    title: "Integrations & API",
    sub: "Connect PROGÈRE Devis AI to your upcoming PROGÈRE ERP or other systems through a secure REST API.",
    create_title: "New API key",
    name_label: "Name (e.g. Production ERP)",
    webhook_label: "Webhook URL (optional — to receive events in real time)",
    webhook_help: "Events sent: quote.accepted · invoice.created · invoice.paid",
    create_btn: "Generate key",
    new_key_title: "Your API key",
    new_key_warn: "⚠️ Copy it now — for security reasons it will never be shown again in clear.",
    copy: "Copy",
    copied: "Copied ✓",
    existing: "Existing keys",
    no_keys: "No API key yet. Create your first key above.",
    last_used: "Last used",
    never: "Never",
    revoked: "Revoked",
    revoke: "Revoke",
    created: "Created",
    docs_title: "API documentation",
    docs_sub: "Endpoints available for your ERP",
    base_url: "Base URL",
    auth_help: "Authentication: HTTP header",
    show_examples: "Show cURL examples",
    hide_examples: "Hide examples",
    endpoints: [
      { method: "GET", path: "/me", desc: "Connected account info" },
      { method: "GET", path: "/quotes", desc: "List quotes (optional status param)" },
      { method: "GET", path: "/invoices", desc: "List invoices with computed status" },
      { method: "GET", path: "/clients", desc: "List clients" },
      { method: "GET", path: "/projects", desc: "List projects" },
    ],
    events_title: "Webhook events",
    events: [
      { name: "quote.accepted", desc: "A client accepted a quote" },
      { name: "invoice.created", desc: "An invoice was generated" },
      { name: "invoice.paid", desc: "An invoice was fully paid" },
    ],
  };

  return (
    <div data-testid="integrations-page">
      <PageHeader title={T.title} subtitle={T.sub} />

      {/* Hero: one-click ERP auto-connect */}
      <div className="glass-strong p-6 mb-6 max-w-5xl glow-gold" data-testid="auto-connect-hero">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(212,175,55,0.18)", border: "1px solid rgba(212,175,55,0.4)" }}>
            <Zap size={22} color="#D4AF37" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg font-bold mb-1" style={{ color: "#F5F5F5" }}>
              {hasAutoKey
                ? (lang === "fr" ? "ERP PROGÈRE connecté automatiquement ✓" : "PROGÈRE ERP auto-connected ✓")
                : (lang === "fr" ? "Connecter mon ERP en 1 clic" : "Connect my ERP in 1 click")}
            </h3>
            <p className="text-xs" style={{ color: "rgba(245,245,245,0.65)" }}>
              {hasAutoKey
                ? (lang === "fr"
                    ? "Vos soumissions, factures et paiements sont synchronisés en temps réel vers PROGÈRE Gestion Chantier. Aucune action requise de votre part."
                    : "Your quotes, invoices and payments are synced in real time to PROGÈRE ERP. No action required.")
                : (lang === "fr"
                    ? "Génère automatiquement votre clé API + connecte les webhooks à PROGÈRE Gestion Chantier. Tout votre flux (devis → facture → paiement) est synchronisé en temps réel."
                    : "Auto-generates your API key + wires the webhooks to PROGÈRE ERP. Your full flow (quote → invoice → payment) is synced in real time.")}
            </p>
          </div>
          {!hasAutoKey && (
            <button onClick={autoConnect} disabled={autoConnecting} className="btn-gold flex-shrink-0" data-testid="auto-connect-btn">
              {autoConnecting ? <><Spinner /> {lang === "fr" ? "Connexion..." : "Connecting..."}</> : <><Zap size={14} /> {lang === "fr" ? "Connecter mon ERP" : "Connect my ERP"}</>}
            </button>
          )}
        </div>
      </div>

      {/* New key form */}
      <form onSubmit={create} className="glass-card p-6 mb-6 max-w-5xl" data-testid="integration-form">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(212,175,55,0.12)" }}>
            <Key size={18} color="#D4AF37" />
          </div>
          <h3 className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>{T.create_title}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <input className="input-dark" placeholder={T.name_label} required value={name} onChange={(e) => setName(e.target.value)} data-testid="integration-name" />
          <input className="input-dark" placeholder="https://votre-erp.com/webhooks/progere" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} data-testid="integration-webhook" />
        </div>
        <p className="text-xs mb-4" style={{ color: "rgba(245,245,245,0.5)" }}>{T.webhook_help}</p>
        <button type="submit" disabled={creating} className="btn-red text-sm" data-testid="integration-create">
          {creating ? <Spinner /> : <><Plus size={14} /> {T.create_btn}</>}
        </button>
      </form>

      {/* New key reveal */}
      {newKey && (
        <div className="p-5 mb-6 max-w-5xl" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 16 }} data-testid="integration-reveal">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 size={18} color="#4ADE80" />
            <div className="font-heading text-base font-bold" style={{ color: "#4ADE80" }}>{T.new_key_title}</div>
          </div>
          <p className="text-xs mb-3" style={{ color: "#FFA500" }}>{T.new_key_warn}</p>
          <div className="flex gap-2">
            <input className="input-dark text-xs font-mono flex-1" readOnly value={newKey.key} data-testid="integration-reveal-key" />
            <button onClick={copyKey} className="btn-red text-sm" data-testid="integration-reveal-copy">
              {copied ? <><CheckCircle2 size={14} /> {T.copied}</> : <><Copy size={14} /> {T.copy}</>}
            </button>
          </div>
        </div>
      )}

      {/* Existing keys */}
      <div className="glass-card p-6 mb-6 max-w-5xl" data-testid="integration-list">
        <h3 className="font-heading text-base font-bold mb-4" style={{ color: "#D4AF37" }}>{T.existing}</h3>
        {!keys ? <Spinner /> : keys.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: "rgba(245,245,245,0.5)" }}>{T.no_keys}</p>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => (
              <div key={k.id} className="flex items-center justify-between p-4 rounded-xl flex-wrap gap-2"
                style={{ background: k.revoked ? "rgba(192,0,0,0.05)" : "rgba(212,175,55,0.04)", border: `1px solid ${k.revoked ? "rgba(192,0,0,0.2)" : "rgba(212,175,55,0.15)"}` }}
                data-testid={`integration-key-${k.id}`}>
                <div className="flex items-center gap-3">
                  <Key size={16} color={k.revoked ? "rgba(245,245,245,0.4)" : "#D4AF37"} />
                  <div>
                    <div className="font-heading text-sm font-bold flex items-center gap-2" style={{ color: k.revoked ? "rgba(245,245,245,0.5)" : "#F5F5F5" }}>
                      {k.name}
                      {k.auto_provisioned && !k.revoked && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1"
                          style={{ background: "rgba(212,175,55,0.18)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.35)" }}>
                          <Zap size={9} /> AUTO
                        </span>
                      )}
                      {k.revoked && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "rgba(192,0,0,0.2)", color: "#FF6B6B" }}>{T.revoked}</span>}
                    </div>
                    <div className="text-xs font-mono mt-0.5" style={{ color: "rgba(245,245,245,0.5)" }}>
                      {k.key_prefix}••••••••• · {T.created} {new Date(k.created_at).toLocaleDateString()}
                    </div>
                    {k.webhook_url && (
                      <div className="text-[11px] mt-1 flex items-center gap-1" style={{ color: "rgba(245,245,245,0.5)" }}>
                        <Webhook size={10} /> {k.webhook_url}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs" style={{ color: "rgba(245,245,245,0.5)" }}>
                    {T.last_used}: {k.last_used_at ? new Date(k.last_used_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "short", timeStyle: "short" }) : T.never}
                  </span>
                  {!k.revoked && (
                    <button onClick={() => revoke(k.id)} className="p-2 rounded-full hover:bg-white/5" data-testid={`integration-revoke-${k.id}`}>
                      <Trash2 size={14} color="#FF6B6B" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documentation */}
      <div className="glass-card p-6 max-w-5xl" data-testid="integration-docs">
        <div className="flex items-center gap-2 mb-1">
          <Code size={18} color="#D4AF37" />
          <h3 className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>{T.docs_title}</h3>
        </div>
        <p className="text-sm mb-5" style={{ color: "rgba(245,245,245,0.6)" }}>{T.docs_sub}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5 text-xs">
          <div className="p-3 rounded-lg font-mono" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.15)" }}>
            <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.4)" }}>{T.base_url}</div>
            <div style={{ color: "#D4AF37" }}>{apiBase}</div>
          </div>
          <div className="p-3 rounded-lg font-mono" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.15)" }}>
            <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.4)" }}>{T.auth_help}</div>
            <div style={{ color: "#D4AF37" }}>X-API-Key: pgr_...</div>
          </div>
        </div>

        <div className="space-y-2 mb-6">
          {T.endpoints.map((ep) => (
            <div key={ep.path} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: "rgba(245,245,245,0.02)" }}>
              <span className="px-2 py-0.5 rounded font-mono text-[10px] font-bold" style={{ background: "rgba(123,174,250,0.15)", color: "#7BAEFA" }}>{ep.method}</span>
              <code className="font-mono text-sm" style={{ color: "#D4AF37" }}>{ep.path}</code>
              <span className="text-xs" style={{ color: "rgba(245,245,245,0.6)" }}>{ep.desc}</span>
            </div>
          ))}
        </div>

        <h4 className="font-heading text-sm font-bold mb-3" style={{ color: "#D4AF37" }}>{T.events_title}</h4>
        <div className="space-y-2 mb-6">
          {T.events.map((e) => (
            <div key={e.name} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: "rgba(245,245,245,0.02)" }}>
              <Webhook size={14} color="#4ADE80" />
              <code className="font-mono text-sm" style={{ color: "#4ADE80" }}>{e.name}</code>
              <span className="text-xs" style={{ color: "rgba(245,245,245,0.6)" }}>{e.desc}</span>
            </div>
          ))}
        </div>

        <button onClick={() => setShowCurl(!showCurl)} className="btn-ghost text-xs" data-testid="integration-toggle-curl">
          <Code size={12} /> {showCurl ? T.hide_examples : T.show_examples}
        </button>

        {showCurl && (
          <pre className="mt-4 p-4 rounded-lg text-xs overflow-x-auto" style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(212,175,55,0.15)", color: "#F5F5F5" }} data-testid="integration-curl">
{`# Récupérer les factures impayées
curl "${apiBase}/invoices?status=unpaid" \\
  -H "X-API-Key: pgr_VOTRE_CLE_ICI"

# Récupérer les soumissions approuvées
curl "${apiBase}/quotes?status=approved" \\
  -H "X-API-Key: pgr_VOTRE_CLE_ICI"

# Webhook payload (envoyé à votre URL configurée)
{
  "event": "invoice.paid",
  "user_id": "...",
  "timestamp": "2026-05-20T14:32:18Z",
  "data": {
    "invoice_id": "...",
    "total": 1500.00
  }
}`}
          </pre>
        )}
      </div>
    </div>
  );
}
